import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';

// material-ui
import { useTheme, styled } from '@mui/material/styles';
import {
  Avatar,
  Box,
  ButtonBase,
  Card,
  Grid,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  OutlinedInput,
  Popover,
  Popper,
  Tooltip
} from '@mui/material';

// third-party
import PopupState, { bindPopper, bindToggle } from 'material-ui-popup-state';

// project imports
import Transitions from 'ui-component/extended/Transitions';

// assets
import { IconSearch, IconX } from '@tabler/icons';
import { shouldForwardProp } from '@mui/system';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import ProjectsFirestoreService from 'views/Projects/ServiceProject';
import { useDispatch, useSelector } from 'react-redux';
import { SET_PROJECT } from 'store/actions';
import { useNavigate } from 'react-router';
import DependencyService from 'views/DependenciesManagement/DependencyService';

// styles
const PopperStyle = styled(Popper, { shouldForwardProp })(({ theme }) => ({
  zIndex: 1100,
  width: '99%',
  top: '-55px !important',
  padding: '0 12px',
  [theme.breakpoints.down('sm')]: {
    padding: '0 10px'
  }
}));

const OutlineInputStyle = styled(OutlinedInput, { shouldForwardProp })(({ theme }) => ({
  width: 434,
  marginLeft: 16,
  paddingLeft: 16,
  paddingRight: 16,
  '& input': {
    background: 'transparent !important',
    paddingLeft: '4px !important'
  },
  [theme.breakpoints.down('lg')]: {
    width: 250
  },
  [theme.breakpoints.down('md')]: {
    width: '100%',
    marginLeft: 4,
    background: '#fff'
  }
}));

const HeaderAvatarStyle = styled(Avatar, { shouldForwardProp })(({ theme }) => ({
  ...theme.typography.commonAvatar,
  ...theme.typography.mediumAvatar,
  background: theme.palette.secondary.light,
  color: theme.palette.secondary.dark,
  '&:hover': {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light
  }
}));

// ==============================|| SEARCH INPUT - MOBILE||============================== //

const MobileSearch = ({ value, setValue, popupState }) => {
  const theme = useTheme();

  return (
    <OutlineInputStyle
      id="input-search-header"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      placeholder="Buscar"
      startAdornment={
        <InputAdornment position="start">
          <IconSearch stroke={1.5} size="1rem" color={theme.palette.grey[500]} />
        </InputAdornment>
      }
      endAdornment={
        <InputAdornment position="end">
          <Box sx={{ ml: 2 }}>
            <ButtonBase sx={{ borderRadius: '12px' }} onClick={() => setValue('')}>
              <Avatar
                variant="rounded"
                sx={{
                  ...theme.typography.commonAvatar,
                  ...theme.typography.mediumAvatar,
                  background: theme.palette.orange.light,
                  color: theme.palette.orange.dark,
                  '&:hover': {
                    background: theme.palette.orange.dark,
                    color: theme.palette.orange.light
                  }
                }}
                {...bindToggle(popupState)}
              >
                <IconX stroke={1.5} size="1.3rem" />
              </Avatar>
            </ButtonBase>
          </Box>
        </InputAdornment>
      }
      aria-describedby="search-helper-text"
      inputProps={{ 'aria-label': 'weight' }}
    />
  );
};

MobileSearch.propTypes = {
  value: PropTypes.string,
  setValue: PropTypes.func,
  popupState: PopupState
};

// ==============================|| SEARCH INPUT ||============================== //

const SearchSection = () => {
  const theme = useTheme();
  const searchRef = useRef(null);
  const { user } = useSelector((state) => state.user);
  const [value, setValue] = useState('');
  const [projects, setProjects] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const { role, dependence } = JSON.parse(user?.reloadUserInfo?.customAttributes);

      const dependenciesChildren = await DependencyService.getDependenciesChildren(dependence);
      const dependencies = [...dependenciesChildren.map((item) => item.dependence), dependence];

      const handleProjects = (data) => {
        setProjects(data);
      };

      const unsubscribe = ProjectsFirestoreService.subscribeToProjects(
        handleProjects,
        role === 'USER_ROLE'
          ? {
              email: user.email,
              dependencies
            }
          : {}
      );

      return () => {
        unsubscribe();
      };
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (value) {
      setAnchorEl(searchRef.current);
    } else {
      setAnchorEl(null);
    }
  }, [value]);

  const goToDetail = (project) => {
    dispatch({ type: SET_PROJECT, project });
    setAnchorEl(null);
    setValue('');
    navigate('/detail-project');
  };

  return (
    <>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <PopupState variant="popper" popupId="demo-popup-popper">
          {(popupState) => (
            <>
              <Box sx={{ ml: 2 }}>
                <ButtonBase sx={{ borderRadius: '12px' }}>
                  <HeaderAvatarStyle variant="rounded" {...bindToggle(popupState)}>
                    <IconSearch stroke={1.5} size="1.2rem" />
                  </HeaderAvatarStyle>
                </ButtonBase>
              </Box>
              <PopperStyle {...bindPopper(popupState)} transition>
                {({ TransitionProps }) => (
                  <>
                    <Transitions type="zoom" {...TransitionProps} sx={{ transformOrigin: 'center top-right' }}>
                      <Card
                        sx={{
                          background: '#fff',
                          [theme.breakpoints.down('sm')]: {
                            border: 0,
                            boxShadow: 'none'
                          }
                        }}
                      >
                        <Box ref={searchRef} sx={{ p: 2 }}>
                          <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item>
                              <MobileSearch value={value} setValue={setValue} popupState={popupState} />
                            </Grid>
                          </Grid>
                        </Box>
                      </Card>
                    </Transitions>
                  </>
                )}
              </PopperStyle>
            </>
          )}
        </PopupState>
      </Box>
      <Box ref={searchRef} sx={{ display: { xs: 'none', md: 'block' } }}>
        <OutlineInputStyle
          id="input-search-header"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Buscar proyecto"
          endAdornment={
            <InputAdornment position="end">
              <ButtonBase sx={{ borderRadius: '12px' }}>
                <HeaderAvatarStyle variant="rounded">
                  <IconSearch stroke={1.5} size="1.3rem" />
                </HeaderAvatarStyle>
              </ButtonBase>
            </InputAdornment>
          }
          aria-describedby="search-helper-text"
          inputProps={{ 'aria-label': 'weight' }}
        />
      </Box>
      <Popover
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        hideBackdrop
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        style={{ pointerEvents: 'none' }}
      >
        <List
          sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper', pointerEvents: 'all' }}
          component="nav"
          aria-labelledby="subheader"
          subheader={
            <ListSubheader component="div" id="subheader">
              Proyectos
            </ListSubheader>
          }
        >
          {projects
            .filter((project) => {
              const lowerValue = value.toLowerCase();

              const matchesSequence = project?.sequence?.toLowerCase().includes(lowerValue) || false;

              const matchesResponsable = project?.responsable?.displayName
                ? project?.responsable?.displayName.toLowerCase().includes(lowerValue)
                : false;

              const matchesResponsableMail = project?.responsable?.email
                ? project?.responsable?.email.toLowerCase().includes(lowerValue)
                : false;

              const matchesEntidad = project?.ministerio ? project?.ministerio.toLowerCase().includes(lowerValue) : false;

              const matchesStatus = project?.status ? project?.status.toLowerCase().includes(lowerValue) : false;

              const matchesTitle = project?.title ? project?.title.toLowerCase().includes(lowerValue) : false;

              return matchesSequence || matchesResponsable || matchesResponsableMail || matchesEntidad || matchesStatus || matchesTitle;
            })
            .map((project, index) => (
              <Tooltip key={index} title={project?.title} placement="right">
                <ListItemButton onClick={() => goToDetail(project)}>
                  <ListItemIcon>
                    <BookmarksIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={project?.sequence}
                    secondary={
                      <>
                        {project?.title && (
                          <span>
                            <strong>Título:</strong> {project?.title}
                            <br />
                          </span>
                        )}
                        {project?.responsable?.displayName && (
                          <span>
                            <strong>Responsable:</strong> {project?.responsable?.displayName}
                            <br />
                          </span>
                        )}
                        {project?.ministerio && (
                          <span>
                            <strong>Ministerio:</strong> {project?.ministerio}
                            <br />
                          </span>
                        )}
                        {project?.status && (
                          <span>
                            <strong>Estado:</strong> {project?.status}
                          </span>
                        )}
                      </>
                    }
                  />
                </ListItemButton>
              </Tooltip>
            ))}
        </List>
      </Popover>
    </>
  );
};

export default SearchSection;
